import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { confirmSheet } from "@/components/ui/action-sheet";
import { fmtAt } from "@/lib/crm";

export function LearnedMatchesSection() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/settings/learned-matches").then((r) => setRows(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  const del = async (m) => {
    if (!(await confirmSheet({ title: `Forget "${m.key}"?`, message: "New readings with this wording will be matched automatically again.", confirm: "Delete", destructive: true }))) return;
    try { await api.delete(`/settings/learned-matches/${m.id}`); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <section className="space-y-3" data-testid="learned-matches">
      <div><h2 className="text-[22px] font-bold text-black">Learned matches</h2>
        <p className="text-[15px] text-[#6D6D72]">When someone changes a line's match, the source wording is remembered and used next time.</p></div>
      <div className="overflow-hidden rounded-[12px] bg-white">
        {rows?.map((m, i) => (
          <div key={m.id} data-testid={`learned-match-${i}`} className="flex items-center justify-between gap-3 border-b border-black/[0.08] px-4 py-2.5 last:border-0">
            <div className="min-w-0"><p className="truncate text-[15px] font-medium text-black">"{m.key}" → {m.line_name}{m.catalog_name ? ` · ${m.catalog_name}` : ""}</p>
              <p className="text-[12px] text-[#6D6D72]">{m.by} · {fmtAt(m.updated_at)} · used {m.uses}×</p></div>
            <button data-testid={`learned-match-delete-${i}`} onClick={() => del(m)} aria-label="Delete" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#D70015] hover:bg-[#FF3B30]/10"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {rows && !rows.length && <p data-testid="learned-matches-empty" className="px-4 py-6 text-center text-[15px] text-[#6D6D72]">No corrections yet.</p>}
      </div>
    </section>
  );
}

function useMatchSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings/match-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async (patch) => {
    try { const { data } = await api.put("/settings/match-settings", patch); setS(data); toast.success("Saved"); } catch (e) { toast.error(errorMessage(e)); }
  };
  return [s, save];
}

export function DefaultGlassSection() {
  const [s, save] = useMatchSettings();
  const [v, setV] = useState("");
  useEffect(() => { if (s) setV(s.default_glass); }, [s]);
  return (
    <section className="space-y-2" data-testid="default-glass-section">
      <h2 className="text-[22px] font-bold text-black">Default glass</h2>
      <p className="text-[15px] text-[#6D6D72]">Every line read from a plan, schedule or outside estimate gets double-pane argon glass with this description. New and repriced Draft lines use it; a glass someone picked on a line is kept.</p>
      <textarea data-testid="default-glass-input" value={v} onChange={(e) => setV(e.target.value)} rows={2} maxLength={300}
        className="block w-full rounded-[12px] border border-black/10 bg-white px-4 py-3 text-[15px] text-black outline-none focus:border-[#007AFF]" />
      <button data-testid="default-glass-save" disabled={!s || !v.trim() || v === s.default_glass} onClick={() => save({ default_glass: v })}
        className="min-h-[44px] rounded-full bg-[#0071E3] px-5 text-[15px] font-semibold text-white disabled:opacity-40">Save</button>
    </section>
  );
}

export function PdfPhotosToggle() {
  const [s, save] = useMatchSettings();
  if (!s) return null;
  return (
    <label data-testid="pdf-photos-toggle-row" className="flex min-h-[52px] items-center justify-between gap-3 rounded-[12px] bg-white px-4 text-[15px] text-black">
      Show product photos on PDF
      <Switch data-testid="pdf-photos-toggle" checked={s.pdf_show_photos} onCheckedChange={(v) => save({ pdf_show_photos: v })} />
    </label>
  );
}

export function Pdf3dToggle() {
  const [s, save] = useMatchSettings();
  if (!s) return null;
  return (
    <label data-testid="pdf-3d-toggle-row" className="flex min-h-[52px] items-center justify-between gap-3 rounded-[12px] bg-white px-4 text-[15px] text-black">
      Show 3D image on PDF
      <Switch data-testid="pdf-3d-toggle" checked={s.pdf_show_3d !== false} onCheckedChange={(v) => save({ pdf_show_3d: v })} />
    </label>
  );
}
